import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import CheckBox from "app/components/inputs/CheckBox";
import ToolTip from "app/routes/Scheduling/ToolTip";
import SwitchToggle from "app/components/OptionButtonGroup/SwitchToggle";
import OnlinePayment from "../../../public/images/online-payment.svg";
import OfflinePayment from "../../../public/images/offline-payment.svg";
import styles from "../PaymentMethodSelector/styles.module.scss";

export type PaymentMethodValue = {
  collectType: "online" | "offline" | "both" | "package-only";
  onlineDefault: boolean;
};

export const DEFAULT_PAYMENT_VALUE: PaymentMethodValue = {
  collectType: "offline",
  onlineDefault: false,
};

export function toPaymentPayload(value: PaymentMethodValue) {
  return {
    collect_type: value.collectType === "package-only" ? "only_package" as const : value.collectType,
    online_default:
      value.collectType === "online" ||
      (value.collectType === "both" && value.onlineDefault),
  };
}

export function fromPaymentModel(opts: {
  collectType?: "online" | "offline" | "both" | "package-only" | "only_package";
  onlineDefault?: boolean;
  packageOnly?: boolean;
}): PaymentMethodValue {
  // Handle both the old packageOnly boolean and the new only_package collect_type
  if (opts.packageOnly || opts.collectType === "only_package") {
    return { collectType: "package-only", onlineDefault: false };
  }

  return {
    collectType: (opts.collectType ?? "offline") as "online" | "offline" | "both" | "package-only",
    onlineDefault: opts.onlineDefault ?? false,
  };
}

type Props = {
  value: PaymentMethodValue;
  onChange: (value: PaymentMethodValue) => void;
  disabled?: boolean;
  forceOnlineDefault?: boolean;
  disableOnline?: boolean;
  disableOffline?: boolean;
  disabledReason?: string;
  collectOfflineNote?: string;
  showPackageOnly?: boolean;
};

export default function PayMethodSelector({
  value,
  onChange,
  disabled = false,
  forceOnlineDefault = false,
  disableOnline = false,
  disableOffline = false,
  disabledReason,
  collectOfflineNote,
  showPackageOnly = false,
}: Props) {
  const { t } = useTranslation();

  const collectOnline =
    value.collectType === "online" || value.collectType === "both";
  const collectOffline =
    value.collectType === "offline" || value.collectType === "both";
  const packageOnly = value.collectType === "package-only";

  const isOnlineDefault =
    (collectOnline && !collectOffline) ||
    (collectOnline && collectOffline && value.onlineDefault);
  const isOfflineDefault =
    (collectOffline && !collectOnline) ||
    (collectOnline && collectOffline && !value.onlineDefault);

  useEffect(() => {
    if (forceOnlineDefault && collectOnline && !value.onlineDefault) {
      onChange({ ...value, onlineDefault: true });
    }
  }, [collectOnline, forceOnlineDefault]);

  function handleCollectOnlineChange(checked: boolean) {
    if (checked) {
      onChange({
        collectType: collectOffline ? "both" : "online",
        onlineDefault: forceOnlineDefault ? true : value.onlineDefault,
      });
    } else if (collectOffline) {
      onChange({ collectType: "offline", onlineDefault: false });
    }
  }

  function handleCollectOfflineChange(checked: boolean) {
    if (checked) {
      onChange({
        collectType: collectOnline ? "both" : "offline",
        onlineDefault: value.onlineDefault,
      });
    } else if (collectOnline) {
      onChange({ collectType: "online", onlineDefault: true });
    }
  }

  function handleOnlineDefaultChange(v: boolean) {
    onChange({ ...value, onlineDefault: v });
  }

  function handlePackageOnlyChange(v: boolean) {
    if (v) {
      onChange({ collectType: "package-only", onlineDefault: false });
    } else {
      // When turning off Package Only, default to offline
      // User can then enable online if desired
      onChange({
        collectType: "offline",
        onlineDefault: false,
      });
    }
  }

  return (
    <div className={styles.container}>
      <p className={styles.payment_method_text}>{t("Payment Method")}</p>

      <div className={styles.rowItem}>
        <div className={styles.left}>
          <CheckBox
            className={styles.checkbox_container}
            name="collectOnline"
            testId="collect-online-checkbox"
            checked={collectOnline}
            onChange={handleCollectOnlineChange}
            squareCheckIcons
            disabled={disabled || disableOnline || packageOnly}
            label={
              <div className={styles.checkbox_label_container}>
                <img src={OnlinePayment} alt="Online" />
                <span className={styles.labelWithTooltip}>
                  {t("Collect Online")}
                  <ToolTip
                    text={
                      disabledReason || t("You will pay online with Stripe.")
                    }
                    orientation="center"
                    fontSize="18px"
                  />
                </span>
              </div>
            }
          />
        </div>
        {isOnlineDefault && (
          <div className={styles.right}>
            <span className={styles.defaultTag}>{t("Default")}</span>
          </div>
        )}
      </div>

      <div className={styles.rowItem}>
        <div className={styles.left}>
          <CheckBox
            className={styles.checkbox_container}
            name="collectOffline"
            testId="collect-offline-checkbox"
            checked={collectOffline}
            onChange={handleCollectOfflineChange}
            squareCheckIcons
            disabled={disabled || disableOffline || packageOnly}
            label={
              <div className={styles.checkbox_label_container}>
                <img src={OfflinePayment} alt="Offline" />
                <span className={styles.normalText}>
                  {t("Collect Offline")}
                </span>
                {collectOfflineNote && (
                  <ToolTip
                    text={collectOfflineNote}
                    orientation="right"
                    fontSize="18px"
                  />
                )}
              </div>
            }
          />
        </div>
        {isOfflineDefault && (
          <div className={styles.right}>
            <span className={styles.defaultTag}>{t("Default")}</span>
          </div>
        )}
      </div>

      {collectOnline && !forceOnlineDefault && (
        <div className={styles.toggleRow}>
          <SwitchToggle
            checked={
              collectOnline && !collectOffline ? true : value.onlineDefault
            }
            disabled={disabled || (collectOnline && !collectOffline)}
            title={t("Set Online as Default")}
            onChange={handleOnlineDefaultChange}
          />
        </div>
      )}

      {showPackageOnly && (
        <div className={styles.toggleRow}>
          <SwitchToggle
            data-testid="collect-package-only-toggle"
            checked={packageOnly}
            title={t("Collect Package Only")}
            onChange={handlePackageOnlyChange}
          />
        </div>
      )}
    </div>
  );
}
